import { resolveDataFieldType } from '../../../helpers/dataFields/dataFieldTypes'
import { concreteOptions } from '../../../helpers/multipleOptions/otherOption'
import isCheckboxChecked from '../../../helpers/intakeForms/isCheckboxChecked'
import housingTypeOptions from '../../../helpers/housingTypes'

/**
 * @typedef {object} AntwoordOptie
 * @property {string} docid - Firestore-document-id van de optie
 * @property {string} Option - De tekst die de gebruiker kiest
 */

/**
 * De invoer van één gegevensregel, in de vorm die bij het veldtype hoort.
 *
 * Eén component voor de gegevenstabel van huishoudens én die van personas.
 * Twee kopieën lopen binnen een paar weken uiteen, en dan slaat hetzelfde
 * veldtype op de twee schermen iets anders op.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * De opslagvorm is gelijk aan die van het intakeformulier: een vinkje als de
 * tekst 'true'/'false', een meervoudige keuze kommagescheiden. Anders zijn
 * dezelfde gegevens uit een formulier en uit de tabel niet meer met elkaar te
 * vergelijken.
 *
 * @param {object} props
 * @param {object} props.item - De regel uit PersonaData
 * @param {(docid: string, value: string) => void} props.onChange - Krijgt de nieuwe waarde
 * @param {Array<AntwoordOptie>} [props.options] - Antwoordopties bij een meerkeuzeveld
 * @param {(docid: string, file: File) => void} [props.onUpload] - Krijgt een gekozen bestand
 * @param {boolean} [props.canEdit] - Of de gebruiker mag aanpassen
 * @param {Array<string>} [props.genderOptions] - Keuzes voor het veld Geslacht
 * @param {Record<string, string>} [props.genderLabels] - Nederlandse namen daarvoor
 * @returns {JSX.Element}
 */
const DataFieldValue = ({
    item,
    onChange,
    options = [],
    onUpload,
    canEdit = true,
    genderOptions = [],
    genderLabels = {},
}) => {
    const type = resolveDataFieldType(item)
    const docid = item?.docid
    const waarde = item?.Value || ''

    const wijzig = (nieuw) => onChange(docid, nieuw)

    if (type === 'checkbox') {
        return (
            <input
                type="checkbox"
                className="data-field-checkbox"
                disabled={!canEdit}
                checked={isCheckboxChecked(waarde)}
                onChange={(e) => wijzig(e.target.checked ? 'true' : 'false')}
            />
        )
    }

    // Een datumkiezer in plaats van een tekstveld, zodat de notatie vastligt.
    // `value` en niet `defaultValue`: bij een datumkiezer typt niemand teken
    // voor teken, dus de cursor kan hier niet terugspringen, en zo volgt het
    // veld wél een wijziging die van elders komt.
    if (type === 'date') {
        return (
            <input
                type="date"
                aria-label="Datum"
                disabled={!canEdit}
                value={waarde}
                onChange={(e) => wijzig(e.target.value)}
            />
        )
    }

    if (type === 'upload') {
        return (
            <div className="data-field-upload">
                {waarde && (
                    <a href={waarde} target="_blank" rel="noreferrer" download>
                        {item?.Title || 'Download'}
                    </a>
                )}
                {canEdit && onUpload && (
                    <input
                        type="file"
                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) onUpload(docid, file)
                        }}
                    />
                )}
            </div>
        )
    }

    if (type === 'multiple-one' || type === 'multiple-many') {
        // Een lege keuzelijst laat de gebruiker met lege handen achter zonder
        // uit te leggen waarom; zeg dus wat er ontbreekt.
        if (!options.length) {
            return <span className="data-field-hint">Dit veld heeft nog geen antwoordopties.</span>
        }

        // De markering van een 'Anders, namelijk...'-optie hoort bij het
        // formulier, niet hier: als keuze zou hij letterlijk als 'other' in de
        // lijst staan. Wat de invuller er zélf typte is een gewone waarde en
        // wordt hieronder gewoon getoond.
        const keuzes = concreteOptions(options)
        const vasteWaarden = keuzes.map((optie) => optie.Option)

        if (type === 'multiple-one') {
            // Een vrij antwoord matcht geen enkele optie. Zonder deze regel toont
            // de keuzelijst een leeg vakje terwijl het antwoord in de database
            // staat, en leest het scherm als 'niets ingevuld'.
            // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
            const vrijAntwoord = waarde && !vasteWaarden.includes(waarde) ? waarde : ''

            return (
                <select
                    disabled={!canEdit}
                    value={waarde}
                    onChange={(e) => wijzig(e.target.value)}
                >
                    <option value="">-- Kies --</option>
                    {keuzes.map((optie) => (
                        <option key={optie.docid} value={optie.Option}>{optie.Option}</option>
                    ))}
                    {vrijAntwoord && (
                        <option value={vrijAntwoord}>{vrijAntwoord} (eigen antwoord)</option>
                    )}
                </select>
            )
        }

        const gekozen = waarde ? waarde.split(',').map((v) => v.trim()).filter(Boolean) : []
        const vrijeAntwoorden = gekozen.filter((v) => !vasteWaarden.includes(v))

        return (
            <div className="data-field-options">
                {keuzes.map((optie) => (
                    <label key={optie.docid} className="data-field-option-label">
                        <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked={gekozen.includes(optie.Option)}
                            onChange={(e) => {
                                const volgende = e.target.checked
                                    ? [...gekozen, optie.Option]
                                    : gekozen.filter((v) => v !== optie.Option)
                                wijzig(volgende.join(', '))
                            }}
                        />
                        {optie.Option}
                    </label>
                ))}
                {/* Hetzelfde verhaal als bij de keuzelijst hierboven: zonder deze
                    regels is een eigen antwoord onzichtbaar. Uitvinken haalt het
                    weg, zoals bij elke andere keuze. */}
                {vrijeAntwoorden.map((antwoord) => (
                    <label key={antwoord} className="data-field-option-label">
                        <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked
                            onChange={() => wijzig(gekozen.filter((v) => v !== antwoord).join(', '))}
                        />
                        {`${antwoord} (eigen antwoord)`}
                    </label>
                ))}
            </div>
        )
    }

    if (type === 'housing') {
        return (
            <select disabled={!canEdit} value={waarde} onChange={(e) => wijzig(e.target.value)}>
                <option value="">-- Selecteer type woning --</option>
                {housingTypeOptions(waarde).map((optie) => (
                    <option key={optie} value={optie}>{optie}</option>
                ))}
            </select>
        )
    }

    if (type === 'gender') {
        return (
            <select disabled={!canEdit} value={waarde} onChange={(e) => wijzig(e.target.value)}>
                <option value="">-- Kies --</option>
                {genderOptions.map((optie) => (
                    <option key={optie} value={optie}>{genderLabels[optie] || optie}</option>
                ))}
                {/* Een nog niet genormaliseerde waarde blijft zichtbaar tot de
                    AI-slag hem omzet. */}
                {waarde && !genderOptions.includes(waarde) && (
                    <option value={waarde}>{waarde} (niet-gestandaardiseerd)</option>
                )}
            </select>
        )
    }

    // Tekst en getal. defaultValue en niet value: de tabel schrijft per
    // toetsaanslag weg, en een gestuurde waarde uit een Firestore-abonnement
    // laat de cursor dan terugspringen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus
    return (
        <input
            type={type === 'number' ? 'number' : 'text'}
            disabled={!canEdit}
            defaultValue={waarde}
            onChange={(e) => wijzig(e.target.value)}
        />
    )
}

export default DataFieldValue
